import {
	FlowDirection,
	JourneyFlowLink,
	JourneyFlowNode,
	JourneyFlowResult,
} from '../../../models/journey-explorer/journey-explorer';
import { COLORS } from './journey-explorer-utils';
import { InteractionInfo, resolveInfo } from './journey-map-layout';

export const BAR_WIDTH = 12;
export const LABEL_WIDTH = 150;
export const MIN_ROW = 30;
const HEADER = 36;
const FOOTER = 20;
const NODE_GAP = 14;
const MIN_BAR = 4;
const MIN_WIDTH = 500;
const MIN_HEIGHT = 300;
const SIDE_PADDING = 20;

export interface FlowNodeLayout {
	node: JourneyFlowNode;
	label: string;
	color: string;
	x: number;
	y: number;
	h: number;
}

export interface FlowColumn {
	x: number;
	step: number;
}

export interface FlowRibbon {
	link: JourneyFlowLink;
	path: string;
}

export interface FlowLayout {
	width: number;
	height: number;
	columns: FlowColumn[];
	nodes: Map<string, FlowNodeLayout>;
	ribbons: FlowRibbon[];
}

export const isInteractionNode = (node: JourneyFlowNode): boolean =>
	node.kind === 'anchor' || node.kind === 'interaction' || node.kind === 'end';

export const flowNodeLabel = (
	node: JourneyFlowNode,
	flow: JourneyFlowResult,
	lookup: Map<string, InteractionInfo>
): string => {
	if (node.kind === 'stop') {
		return flow.direction === 'before' ? 'Journey start' : 'No further activity';
	}
	if (node.kind === 'other') {
		return `${node.other_count} other interactions`;
	}
	if (node.kind === 'not_end') {
		return `Did not reach ${resolveInfo(lookup, flow.end ?? '').label}`;
	}
	return resolveInfo(lookup, node.name ?? '').label;
};

export const flowNodeColor = (node: JourneyFlowNode, lookup: Map<string, InteractionInfo>): string => {
	if (node.kind === 'stop' || node.kind === 'not_end') {
		return COLORS.line;
	}
	if (node.kind === 'other') {
		return COLORS.dim;
	}
	return resolveInfo(lookup, node.name ?? '').color;
};

export const stepTitle = (step: number, flow: Pick<JourneyFlowResult, 'direction' | 'steps'>): string => {
	if (step === 0) {
		return flow.direction === 'before' ? 'End point' : 'Starting point';
	}
	if (flow.direction === 'between' && step > flow.steps) {
		return 'End point';
	}
	const direction: FlowDirection = flow.direction === 'before' ? 'before' : 'after';
	return `${step} ${step === 1 ? 'step' : 'steps'} ${direction}`;
};

export const stepTotal = (flow: JourneyFlowResult, step: number): number =>
	flow.nodes.filter((node) => node.step === step).reduce((total, node) => total + node.customers, 0);

const ribbonPath = (fromX: number, fromY: number, toX: number, toY: number, thickness: number): string => {
	const middle = (fromX + toX) / 2;
	return (
		`M${fromX},${fromY} C${middle},${fromY} ${middle},${toY} ${toX},${toY} ` +
		`L${toX},${toY + thickness} C${middle},${toY + thickness} ${middle},${fromY + thickness} ${fromX},${
			fromY + thickness
		} Z`
	);
};

export const buildFlowLayout = (
	flow: JourneyFlowResult,
	lookup: Map<string, InteractionInfo>,
	size: { w: number; h: number }
): FlowLayout => {
	const width = Math.max(MIN_WIDTH, size.w);
	const height = Math.max(MIN_HEIGHT, size.h);
	const lastStep = flow.nodes.reduce((last, node) => Math.max(last, node.step), 0);
	const byStep: JourneyFlowNode[][] = Array.from({ length: lastStep + 1 }, () => []);
	flow.nodes.forEach((node) => byStep[node.step].push(node));
	const steps = byStep.map((nodes, step) => ({ step, nodes }));
	const ordered = flow.direction === 'before' ? [...steps].reverse() : steps;

	const columnX = (index: number) =>
		SIDE_PADDING + index * ((width - SIDE_PADDING * 2 - LABEL_WIDTH - BAR_WIDTH) / Math.max(1, ordered.length - 1));
	const maxTotal = Math.max(
		1,
		...ordered.map(({ nodes }) => nodes.reduce((total, node) => total + node.customers, 0))
	);
	const maxRows = Math.max(1, ...ordered.map(({ nodes }) => nodes.length));
	const scale = Math.max(0, (height - HEADER - FOOTER - NODE_GAP * (maxRows - 1)) / maxTotal);

	const nodes = new Map<string, FlowNodeLayout>();
	let bottom = 0;
	ordered.forEach(({ nodes: column }, index) => {
		let y = HEADER;
		column.forEach((node) => {
			const h = Math.max(MIN_BAR, node.customers * scale);
			nodes.set(node.key, {
				node,
				label: flowNodeLabel(node, flow, lookup),
				color: flowNodeColor(node, lookup),
				x: columnX(index),
				y,
				h,
			});
			y += Math.max(h, MIN_ROW) + NODE_GAP;
		});
		bottom = Math.max(bottom, y);
	});

	const offsets = new Map<string, { in: number; out: number }>();
	const offset = (key: string) => {
		let current = offsets.get(key);
		if (!current) {
			current = { in: 0, out: 0 };
			offsets.set(key, current);
		}
		return current;
	};
	const ribbons = flow.links
		.map((link) => {
			const source = nodes.get(link.source);
			const target = nodes.get(link.target);
			if (!source || !target) {
				return null;
			}
			return source.x < target.x ? { link, left: source, right: target } : { link, left: target, right: source };
		})
		.filter((item): item is { link: JourneyFlowLink; left: FlowNodeLayout; right: FlowNodeLayout } => item !== null)
		.sort((a, b) => a.left.y - b.left.y || a.right.y - b.right.y)
		.map(({ link, left, right }) => {
			const thickness = Math.max(1, link.customers * scale);
			const leftOffset = offset(left.node.key);
			const rightOffset = offset(right.node.key);
			const fromY = left.y + leftOffset.out;
			const toY = right.y + rightOffset.in;
			leftOffset.out += thickness;
			rightOffset.in += thickness;
			return { link, path: ribbonPath(left.x + BAR_WIDTH, fromY, right.x, toY, thickness) };
		});

	return {
		width,
		height: Math.max(height, bottom + FOOTER),
		columns: ordered.map(({ step }, index) => ({ x: columnX(index), step })),
		nodes,
		ribbons,
	};
};
